import {
    Box, Flex, Text, Button, Divider,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    useDisclosure, HStack, Tag, TagLabel,
} from "@chakra-ui/react";

import { SubscriptionDto, UserDto } from "doxecure-commons/doxecureClientTypes";
import { ProductStructure, WaitingRequestAction } from "doxecure-commons/doxecureTypes";
import { $ok } from "foundation-ts/commons";
import { $timeBetweenDates } from "foundation-ts/date";
import { TSDate } from "foundation-ts/tsdate";
import { Nullable } from "foundation-ts/types";

import { G1Button } from "../G1Button";

import { formatAmount } from "./helpers";
import { Card, ProgressBar } from "./UI";

interface PlanCardProps {
    connectedUser?: Nullable<UserDto>;
    subscription: Nullable<SubscriptionDto>;
    extraUsers: number;
    pricePerExtraUser: number;
    productPackInfos?: ProductStructure;
    waitingRequestAction?: WaitingRequestAction;
    onBuyExtraPack: () => void;
    onCancelTerminationRequest: () => void;
    onChangeToUnlimitedPlan: () => void;
    onStartNow: () => void;
    onTerminate: () => void;
}

export function PlanCard(props: PlanCardProps) {
    const { connectedUser, subscription, waitingRequestAction, extraUsers, pricePerExtraUser, productPackInfos, onBuyExtraPack, onCancelTerminationRequest, onChangeToUnlimitedPlan, onStartNow, onTerminate } = props;
    const changeToUnlimitedPlanModal    = useDisclosure();
    const terminateModal                = useDisclosure();
    const startNowModal                 = useDisclosure();
    const extracPackModal               = useDisclosure();
    const cancelTerminationModal        = useDisclosure();

    const isTrialPeriod = $ok(subscription?.trialPeriod);
    const sigPct      = $ok(subscription) ? (subscription!.signatureCount.subscriptionConsumed / subscription!.signatureCount.subscriptionMax) * 100 : 0;
    const trialLeft = isTrialPeriod ? remainingTrialDays(TSDate.zulu().dateWithoutTime(), subscription!.trialPeriod!.end) : -1;
    const extraUsersPrice = $ok(subscription) ? extraUsers * pricePerExtraUser * (1 + subscription!.vatRate / 100) : 0;
    const priceTTC = $ok(subscription) ? extraUsersPrice + (subscription!.priceHT * (1 + subscription!.vatRate / 100)) : 0;
    const isCancelationRequested = waitingRequestAction === "cancel-subscription";
    const isStartNowRequested = waitingRequestAction === "start-now";
    const isEnableBuyCreditPack = !isTrialPeriod && !subscription?.unlimited; // TODO && pas de pack en cours
    const packPriceTTC = $ok(productPackInfos) ? productPackInfos!.price * (1 + subscription!.vatRate / 100) : 0;

    function handleChangeToUnlimitedPlan(): void {
        onChangeToUnlimitedPlan();
        changeToUnlimitedPlanModal.onClose();
    }

    function handleTerminate(): void {
        onTerminate();
        terminateModal.onClose();
    }

    function handleCancelTerminationRequest(): void {
        onCancelTerminationRequest();
        cancelTerminationModal.onClose();
    }

    function handleStartNow(): void {
        onStartNow();
        startNowModal.onClose();
    }

    function handleBuyExtraPack(): void {
        onBuyExtraPack();
        extracPackModal.onClose();
    }
    function openChangePlan(): void {
        // setSelectedPlanId(sub.plan.id);
        changeToUnlimitedPlanModal.onOpen();
    }

    const actions = [
        {
            show: isTrialPeriod && !isCancelationRequested && !isStartNowRequested,
            label: "Commencer maintenant",
            style: {
                color: "white",
                hoverBg: "brand.600",
                bg: "brand.500",
            },
            onClick: startNowModal.onOpen
        }, 
        {
            show: !isTrialPeriod && !subscription?.unlimited && !isCancelationRequested,
            label: "Passer à l'illimité",
            style: {
                color: "white",
                hoverBg: "brand.600",
                bg: "brand.500",
            },
            onClick: changeToUnlimitedPlanModal.onOpen
        },
        {
            show: !isTrialPeriod && !!subscription?.unlimited && !isCancelationRequested,
            label: "Revenir à l'abonnement standard",
            style: {
                color: "white",
                hoverBg: "brand.600",
                bg: "brand.500",
            },
            onClick: openChangePlan
        },
        {
            show: isCancelationRequested,
            label: "Annuler la résiliation",
            style: {
                color: "red.400",
                hoverBg: "red.50",
                variant: "ghost"
            },
            onClick: cancelTerminationModal.onOpen
        },
        {
            show: !isCancelationRequested && !isStartNowRequested,
            label: "Résilier",
            style: {
                color: "red.400",
                hoverBg: "red.50",
                variant: "ghost"
            },
            onClick: terminateModal.onOpen
        }
    ];

    const tags = [
        {
            show: isTrialPeriod,
            label: `Essai — ${trialLeft > 1 ? `${trialLeft}j restants` : 'dernier jour'}`,
            style: {
                bg: "orange.50",
                borderColor: "orange.200",
                color: "orange.600"
            },
        },
        {
            show: isCancelationRequested,
            label: "Résiliation en cours",
            style: {
                bg: "red.50",
                borderColor: "red.200",
                color: "red.600"
            }
        }
    ]

    return (
        <>
            <Card gridColumn={{ lg: "span 2" }}>
                <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3}>
                    <Box>
                        <HStack spacing={3} gap={2} mb={1} flexWrap="wrap">
                            <Text fontFamily="heading" fontSize="xl" color="gray.900">
                                {subscription?.name}
                            </Text>
                            { tags
                                .filter(({ show }) => show)
                                .map(({ label, style }) => (
                                    <Tag key={label} size="sm" borderRadius="full" bg={style.bg} border="1px solid" borderColor={style.borderColor} px={3} py={1}>
                                        <TagLabel fontSize="xs" color={style.color} fontWeight="600">
                                            {label}
                                        </TagLabel>
                                    </Tag>
                                ))
                            }
                        </HStack>
                        <Text fontSize="sm" color="gray.400">
                            {formatAmount(subscription?.priceHT)} HT / an · 1 mois d'essai ·{" "}
                            {!!subscription?.unlimited ? "Signatures illimitées" : `${subscription?.signaturesPerYear} signatures / an`}
                        </Text>
                        { isCancelationRequested && (
                            <Text fontSize="sm" color="red.400">
                                Résiliation demandée le {subscription?.requestWaintingActionInfos?.requestDate?.toString("%d %B %Y")} par {connectedUser?.apid === subscription?.requestWaintingActionInfos?.requesterId ? "vous-même" : subscription?.requestWaintingActionInfos?.requesterName} ·{" "}
                                {`Votre abonnement prendra donc fin le ${subscription?.requestWaintingActionInfos?.end?.toString("%d %B %Y")}`}
                            </Text> 
                        )}
                        { isStartNowRequested && (
                            <Text fontSize="sm" color="red.400">
                                La demande effectuée par {subscription?.requestWaintingActionInfos?.requesterName} pour démarrer plus tôt l'abonnement a été prise en compte. Un email de confirmation vous sera envoyé dès réception de la validation du réglement (jusqu'à 2 à 3 jours).
                            </Text> 
                        )}
                    </Box>
                    <HStack spacing={2} flexWrap="wrap">
                        { actions
                            .filter(({ show }) => show)
                            .map(({ label, style, onClick }) => (
                                <Button key="label" size="sm" variant={style?.variant ?? "solid"} color={style?.color} bg={style?.bg ?? "transparent"}
                                        _hover={{ bg: style.hoverBg }} onClick={onClick}>
                                    {label}
                                </Button>
                            ))
                        }
                    </HStack>
                </Flex>

                <Divider my={5} borderColor="gray.100" />

                { !subscription?.unlimited ? (
                    <Box mb={isTrialPeriod ? 0 : 5}>
                        <Flex justify="space-between" mb={2}>
                            <Text fontSize="sm" fontWeight="600" color="gray.700">Signatures cette année</Text>
                            <Text fontSize="sm" color={sigPct > 80 ? "red.500" : "gray.500"}>
                                <Text as="span" fontWeight="700" color="gray.800">{subscription?.signatureCount.subscriptionConsumed}</Text>
                                {" / "}{subscription?.signatureCount.subscriptionMax}
                            </Text>
                        </Flex>
                        <ProgressBar value={subscription?.signatureCount.subscriptionConsumed ?? 0} max={subscription?.signatureCount.subscriptionMax ?? 0} color={!!subscription?.unlimited ? "#0d1a5c": "#3354f4"} />
                        {sigPct > 80 && (
                            <Text fontSize="xs" color="red.400" mt={1}>Vous approchez de votre quota annuel.</Text>
                        )}
                    </Box>
                ) : (
                    <Flex align="center" gap={2} mb={0}>
                        <Box w="8px" h="8px" borderRadius="full" bg="green.400" flexShrink={0} />
                        <Text fontSize="sm" color="gray.600">
                            <Text as="span" fontWeight="600" color="gray.800">{subscription?.signatureCount.subscriptionConsumed}</Text>
                            {" "}signatures effectuées cette année · quota illimité
                        </Text>
                    </Flex>
                )}

                { isEnableBuyCreditPack && (
                    <Box bg="brand.50" borderRadius="12px" p={4} border="1px dashed" borderColor="brand.100">
                        <Flex justify="space-between" align="center" gap={3} wrap="wrap">
                            <Box>
                                <Text fontSize="sm" fontWeight="600" color="brand.700">Besoin de signatures supplémentaires ?</Text>
                                <Text fontSize="xs" color="brand.500" mt={0.5}>Achetez un pack de {productPackInfos?.signatures} crédits</Text>
                            </Box>
                            <Button size="sm" bg="brand.500" color="white" _hover={{ bg: "brand.600" }} flexShrink={0} onClick={extracPackModal.onOpen}>
                                Acheter un pack
                            </Button>
                        </Flex>
                    </Box>
                )}
            </Card>

            <Modal isOpen={changeToUnlimitedPlanModal.isOpen} onClose={changeToUnlimitedPlanModal.onClose} size="lg" isCentered>
                <ModalOverlay backdropFilter="blur(6px)" />
                <ModalContent borderRadius="20px" p={2}>
                    <ModalHeader fontFamily="heading">Passer à l'illimité</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {/* <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                            {PLANS.map((plan) => {
                                const active = selectedPlanId === plan.id;
                                return (
                                    <Box key={plan.id} borderRadius="14px" border="2px solid"
                                         borderColor={active ? plan.color : "gray.100"} p={5} cursor="pointer"
                                         bg={active ? plan.accent + "44" : "white"} transition="all 0.2s"
                                         onClick={() => setSelectedPlanId(plan.id)}>
                                        <Text fontWeight="700" fontSize="sm" color={plan.color} mb={1}>{plan.name}</Text>
                                        <Text fontSize="2xl" fontWeight="800" color="gray.900">
                                            {formatAmount(plan.pricePerYear)}
                                            <Text as="span" fontSize="xs" fontWeight="400" color="gray.400"> / an</Text>
                                        </Text>
                                        <Divider my={3} />
                                        <VStack spacing={1} align="start">
                                            <Text fontSize="xs" color="gray.500">
                                                {plan.signaturesPerYear ? `${plan.signaturesPerYear} signatures / an` : "Signatures illimitées"}
                                            </Text>
                                            <Text fontSize="xs" color="gray.500">{plan.usersIncluded} utilisateur inclus</Text>
                                            <Text fontSize="xs" color="gray.500">
                                                +{formatAmount(plan.pricePerExtraUser)} / utilisateur supplémentaire
                                            </Text>
                                            <Text fontSize="xs" color="green.600" fontWeight="500">{plan.trialMonths} mois offert</Text>
                                            {plan.hasCreditPacks && (
                                                <Badge mt={1} colorScheme="blue" fontSize="9px" borderRadius="full">
                                                    Packs de crédits disponibles
                                                </Badge>
                                            )}
                                        </VStack>
                                    </Box>
                                );
                            })}
                        </SimpleGrid> */}
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <G1Button component="form" variant="cancel" title="Annuler" flex={1} onClick={changeToUnlimitedPlanModal.onClose} />
                        <G1Button component="form" variant="important" title="Confirmer le changement" flex={1} onClick={handleChangeToUnlimitedPlan} />
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={terminateModal.isOpen} onClose={terminateModal.onClose} size="md" isCentered>
                <ModalOverlay backdropFilter="blur(6px)" />
                <ModalContent borderRadius="20px" p={2}>
                    <ModalHeader fontFamily="heading" color="red.500">Résilier l'abonnement</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="sm" color="gray.600">
                            Êtes-vous sûr de vouloir résilier votre <strong>{subscription?.name}</strong> ?
                            Vous conservez l'accès jusqu'à la fin de votre période en cours.
                        </Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <G1Button component="form" variant="cancel" title="Conserver" flex={1} onClick={terminateModal.onClose} />
                        <G1Button component="form" variant="delete" title="Résilier" flex={1} onClick={handleTerminate} />
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={cancelTerminationModal.isOpen} onClose={cancelTerminationModal.onClose} size="md" isCentered>
                <ModalOverlay backdropFilter="blur(6px)" />
                <ModalContent borderRadius="20px" p={2}>
                    <ModalHeader fontFamily="heading" color="red.500">Annuler la résiliation</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="sm" color="gray.600">Êtes-vous sûr de vouloir annuler la résiliation ?</Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <G1Button component="form" variant="cancel" title="Non" flex={1} onClick={terminateModal.onClose} />
                        <G1Button component="form" variant="delete" title="Oui" flex={1} onClick={handleCancelTerminationRequest} />
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={startNowModal.isOpen} onClose={startNowModal.onClose} size="md" isCentered>
                <ModalOverlay backdropFilter="blur(6px)" />
                <ModalContent borderRadius="20px" p={2}>
                    <ModalHeader fontFamily="heading">Commencer maintenant</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="sm" color="gray.600">
                            En démarrant votre <strong>{subscription?.name}</strong> dès maintenant,
                            votre période d'essai prendra fin dès réception du réglement et vous serez facturé{" "}
                            {extraUsers > 0 ? `du prix de celui-ci et de l'ajout de ${extraUsers} collaborateurs, soit ` : ""}
                            <strong>{formatAmount(priceTTC)} TTC / an</strong> à partir d'aujourd'hui.
                        </Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <G1Button component="form" variant="cancel" title="Annuler" flex={1} onClick={startNowModal.onClose} />
                        <G1Button component="form" variant="important" title="Démarrer l'abonnement" flex={1} onClick={handleStartNow} />
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={extracPackModal.isOpen} onClose={extracPackModal.onClose} size="sm" isCentered>
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent borderRadius="20px" p={2}>
                    <ModalHeader fontFamily="heading">Acheter un pack de crédits</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="sm" color="gray.600">
                            Le pack contient {productPackInfos?.signatures} signatures au prix de {productPackInfos?.price}€ HT (soit {formatAmount(packPriceTTC)}€ TTC).{" "}
                            Ces crédits sont utilisés à partir du moment où votre abonnement est épuisé et restent valides même après le renouvellement de votre abonnement.
                        </Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <G1Button component="form" variant="cancel" title="Annuler" flex={1} onClick={extracPackModal.onClose} />
                        <G1Button component="form" variant="normal" title="Acheter" flex={1} onClick={handleBuyExtraPack} />
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}

const remainingTrialDays = (a: TSDate, b: TSDate): number => Math.floor(Math.abs($timeBetweenDates(a, b)) / (3600 * 24));